import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'

import { AccountDetailedDto } from '@model/AccountDetailedDto'

import { useDocumentVerificationPath } from '@hooks/useDocumentVerificationPath'

type AccessCallBack = () => void

interface AccountActionAccessCheck {
  checkAccountTradersRoomAccess: (callback: AccessCallBack) => void
  checkAccountOpenAccountAccess: (callback: AccessCallBack) => void
  checkAccountIBAccess: (callback: AccessCallBack) => void
  isAccountTradersRoomActionAccessLocked: boolean
  isAccountOpenAccountActionAccessLocked: boolean
  isAccountIntroducingBrokerActionAccessLocked: boolean
}

export const useAccountActionAccessCheck = (): AccountActionAccessCheck => {
  const navigate = useNavigate()
  const { account } = useAccountReadContext()
  const docVerificationPath = useDocumentVerificationPath()

  const accountActionAccessLocks = getAccountActionAccessLocks(account)

  const checkAccountTradersRoomActionAccess = (callback: AccessCallBack) => {
    checkActionAccess(callback)(accountActionAccessLocks.isAccountTradersRoomActionAccessLocked)
  }

  const checkAccountOpenAccountActionAccess = (callback: AccessCallBack) => {
    checkActionAccess(callback)(accountActionAccessLocks.isAccountOpenAccountActionAccessLocked)
  }

  const checkAccountIBActionAccess = (callback: AccessCallBack) => {
    checkActionAccess(callback)(
      accountActionAccessLocks.isAccountIntroducingBrokerActionAccessLocked
    )
  }

  const checkActionAccess = (callback: AccessCallBack) => (locked: boolean) => {
    locked ? navigate(docVerificationPath) : callback()
  }

  return {
    ...accountActionAccessLocks,
    checkAccountTradersRoomAccess: checkAccountTradersRoomActionAccess,
    checkAccountOpenAccountAccess: checkAccountOpenAccountActionAccess,
    checkAccountIBAccess: checkAccountIBActionAccess,
  }
}

interface AccountActionAccessLocks {
  isAccountTradersRoomActionAccessLocked: boolean
  isAccountOpenAccountActionAccessLocked: boolean
  isAccountIntroducingBrokerActionAccessLocked: boolean
}

export const getAccountActionAccessLocks = (
  account?: AccountDetailedDto
): AccountActionAccessLocks => {
  return {
    isAccountTradersRoomActionAccessLocked: isAccountTradersRoomActionAccessLocked(account),
    isAccountOpenAccountActionAccessLocked: isAccountOpenAccountActionAccessLocked(account),
    isAccountIntroducingBrokerActionAccessLocked:
      isAccountIntroducingBrokerActionAccessLocked(account),
  }
}

export const isAccountTradersRoomActionAccessLocked = (
  account: AccountDetailedDto | undefined
): boolean => {
  return !!account?.actionRestrictions?.tradersRoomRestricted
}

export const isAccountOpenAccountActionAccessLocked = (
  account: AccountDetailedDto | undefined
): boolean => {
  return !!account?.actionRestrictions?.openAccountRestricted
}

export const isAccountIntroducingBrokerActionAccessLocked = (
  account: AccountDetailedDto | undefined
): boolean => {
  return !!account?.actionRestrictions?.introducingBrokerRestricted
}
